import PropTypes from 'prop-types';
import styles from '../../styles/ads.module.scss';
import GenericTable from '../../components/tables/GenericTable';
import { formatPriceWithCurrency } from '../../utils/formatPrice';
import { formatValue } from '../../utils/formatValue';

const SECTION_HEADER_STYLE = { margin: '24px 0 8px' };
const TECHNICAL_HEADER_STYLE = { ...SECTION_HEADER_STYLE, color: '#98A2B3', fontSize: 14 };
const TECHNICAL_ROW_STYLE = { color: '#98A2B3', fontSize: 13 };

const formatPercent = (val) => (val != null ? `${Math.round(val * 100) / 100}%` : '—');

const resolveProductLabel = (productId, products) => {
  if (!productId) return '—';
  const id = (productId?._id ?? productId)?.toString();
  const match = (products ?? []).find(
    (p) => (p.productId?._id ?? p.productId)?.toString() === id
  );
  return match?.name || id;
};

// Only the primary referring doctor (`referral.doctor`) comes populated with a
// name from the API — chain-commission entries below level 1 reference other
// doctors by raw id only, with no second lookup done here (see plan: no new
// per-order fetches in this hotfix).
const resolveDoctorLabel = (doctorId, referralDoctor) => {
  if (!doctorId) return '—';
  const id = (doctorId?._id ?? doctorId)?.toString();
  if (referralDoctor?._id?.toString() === id) {
    return [referralDoctor.firstName, referralDoctor.lastName].filter(Boolean).join(' ') || id;
  }
  return id;
};

const Row = ({ label, children, style }) => (
  <tr>
    <td style={style}>{label}</td>
    <td style={style}>{children}</td>
  </tr>
);

Row.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
  style: PropTypes.object,
};

const SectionTable = ({ title, headerStyle, children }) => (
  <div className="table-wrapper">
    <h3 style={headerStyle}>{title}</h3>
    <table style={{ width: '100%' }} className={`${styles.table} grayTheme mobNoScroll`}>
      <tbody>{children}</tbody>
    </table>
  </div>
);

SectionTable.propTypes = {
  title: PropTypes.string.isRequired,
  headerStyle: PropTypes.object,
  children: PropTypes.node,
};

const chainCommissionColumns = (products, referralDoctor) => [
  { key: 'doctor', label: 'Doktor', render: (val) => resolveDoctorLabel(val, referralDoctor) },
  { key: 'level', label: 'Nivo' },
  { key: 'percent', label: 'Procenat', render: (val) => formatPercent(val) },
  { key: 'amount', label: 'Iznos', render: (val) => formatPriceWithCurrency(val || 0) },
  { key: 'base', label: 'Osnovica', render: (val) => formatPriceWithCurrency(val || 0) },
  { key: 'product', label: 'Proizvod', render: (val) => resolveProductLabel(val, products) },
  { key: 'source', label: 'Izvor' },
];

const agentCommissionColumns = (products) => [
  { key: 'percent', label: 'Procenat', render: (val) => formatPercent(val) },
  { key: 'amount', label: 'Iznos', render: (val) => formatPriceWithCurrency(val || 0) },
  { key: 'base', label: 'Osnovica', render: (val) => formatPriceWithCurrency(val || 0) },
  { key: 'product', label: 'Proizvod', render: (val) => resolveProductLabel(val, products) },
  { key: 'source', label: 'Izvor' },
];

const discountBreakdownColumns = (products) => [
  { key: 'productId', label: 'Proizvod', render: (val) => resolveProductLabel(val, products) },
  { key: 'discountPercent', label: 'Procenat', render: (val) => formatPercent(val) },
  { key: 'discountAmount', label: 'Iznos', render: (val) => formatPriceWithCurrency(val || 0) },
];

const cashbackBreakdownColumns = (products) => [
  { key: 'productId', label: 'Proizvod', render: (val) => resolveProductLabel(val, products) },
  { key: 'percent', label: 'Procenat', render: (val) => formatPercent(val) },
  { key: 'amount', label: 'Iznos', render: (val) => formatPriceWithCurrency(val || 0) },
];

const withSyntheticIds = (rows) => (rows ?? []).map((row, i) => ({ ...row, _id: `${i}` }));

const OrderCommissionsAndDiscounts = ({ order }) => {
  if (!order) return null;

  const { products, discountInfo, productCashbackDeduction, referral } = order;
  const referralDoctor = referral?.doctor;
  const hasChainCommissions = (referral?.chainCommissions ?? []).length > 0;
  const hasAgentCommissions = (referral?.agentCommissions ?? []).length > 0;
  const hasDiscountBreakdown = (discountInfo?.breakdown ?? []).length > 0;
  const hasCashbackBreakdown = (productCashbackDeduction?.breakdown ?? []).length > 0;

  return (
    <>
      <SectionTable title="Popusti i provizije" headerStyle={SECTION_HEADER_STYLE}>
        <Row label="Popust (tip)">{discountInfo?.reason ?? formatValue(undefined)}</Row>
        <Row label="Popust (procenat)">
          {discountInfo?.percentage != null ? formatPercent(discountInfo.percentage) : formatValue(undefined)}
        </Row>
        <Row label="Popust (iznos)">
          {discountInfo?.amount != null ? formatPriceWithCurrency(discountInfo.amount) : formatValue(undefined)}
        </Row>
        <Row label="Cashback odbitak (iznos)">
          {productCashbackDeduction?.amount != null
            ? formatPriceWithCurrency(productCashbackDeduction.amount)
            : formatValue(undefined)}
        </Row>
        <Row label="Referral — doktor">
          {referralDoctor
            ? `${referralDoctor.firstName} ${referralDoctor.lastName} (${referralDoctor.referralCode ?? '—'})`
            : formatValue(null)}
        </Row>
        <Row label="Provizija doktora (ukupno)">{formatPriceWithCurrency(order.commissionDoctor || 0)}</Row>
        <Row label="Provizija agenta (ukupno)">{formatPriceWithCurrency(referral?.commissionAgent || 0)}</Row>
      </SectionTable>

      {hasDiscountBreakdown && (
        <div className="table-wrapper">
          <h4 style={{ margin: '12px 0 8px', fontSize: 14 }}>Popust po proizvodu</h4>
          <GenericTable
            data={withSyntheticIds(discountInfo.breakdown)}
            columns={discountBreakdownColumns(products)}
            grayTheme
          />
        </div>
      )}

      {hasCashbackBreakdown && (
        <div className="table-wrapper">
          <h4 style={{ margin: '12px 0 8px', fontSize: 14 }}>Cashback odbitak po proizvodu</h4>
          <GenericTable
            data={withSyntheticIds(productCashbackDeduction.breakdown)}
            columns={cashbackBreakdownColumns(products)}
            grayTheme
          />
        </div>
      )}

      {hasChainCommissions && (
        <div className="table-wrapper">
          <h4 style={{ margin: '12px 0 8px', fontSize: 14 }}>Lanac provizija (mentori)</h4>
          <GenericTable
            data={withSyntheticIds(referral.chainCommissions)}
            columns={chainCommissionColumns(products, referralDoctor)}
            grayTheme
          />
        </div>
      )}

      {hasAgentCommissions && (
        <div className="table-wrapper">
          <h4 style={{ margin: '12px 0 8px', fontSize: 14 }}>Provizije agenta po proizvodu</h4>
          <GenericTable
            data={withSyntheticIds(referral.agentCommissions)}
            columns={agentCommissionColumns(products)}
            grayTheme
          />
        </div>
      )}

      <SectionTable title="Lojalti i vaučer" headerStyle={SECTION_HEADER_STYLE}>
        <Row label="Iskorišćeni poeni">{order.loyaltyPointsUsed || 0}</Row>
        <Row label="Vrednost iskorišćenih poena">{formatPriceWithCurrency(order.loyaltyPointsAmount || 0)}</Row>
        {order.upsellOffer && (
          <Row label="Upsell ponuda">
            {`${order.upsellOffer.name} — ${formatPriceWithCurrency(order.upsellOffer.finalPrice || 0)} (umesto ${formatPriceWithCurrency(order.upsellOffer.regularPrice || 0)})`}
          </Row>
        )}
        {order.originalSum != null && (
          <Row label="Originalna cena (pre popusta)">{formatPriceWithCurrency(order.originalSum)}</Row>
        )}
      </SectionTable>

      <SectionTable title="Tehnički detalji" headerStyle={TECHNICAL_HEADER_STYLE}>
        <Row label="Izvor porudžbine" style={TECHNICAL_ROW_STYLE}>{order.source ?? formatValue(undefined)}</Row>
        <Row label="Kanal" style={TECHNICAL_ROW_STYLE}>{order.channel ?? formatValue(undefined)}</Row>
        <Row label="Mix4 checkout" style={TECHNICAL_ROW_STYLE}>{formatValue(order.isMix4Checkout)}</Row>
        <Row label="Sadrži spoljni proizvod" style={TECHNICAL_ROW_STYLE}>{formatValue(order.hasExternalProduct)}</Row>
        <Row label="Sadrži zamrznute proizvode" style={TECHNICAL_ROW_STYLE}>{formatValue(order.hasFrozenProducts)}</Row>
        <Row label="Sadrži pakete" style={TECHNICAL_ROW_STYLE}>{formatValue(order.hasPackageProducts)}</Row>
        <Row label="Preuzimanje na licu mesta" style={TECHNICAL_ROW_STYLE}>{formatValue(order.isPickup)}</Row>
        <Row label="Distributer (preuzimanje)" style={TECHNICAL_ROW_STYLE}>
          {order.pickupDistributor
            ? <span>{(order.pickupDistributor?._id ?? order.pickupDistributor)?.toString()} <i>(ID, API ne vraća naziv)</i></span>
            : formatValue(null)}
        </Row>
        <Row label="Izmenio (ID)" style={TECHNICAL_ROW_STYLE}>
          {order.lastEditedBy
            ? <span>{(order.lastEditedBy?._id ?? order.lastEditedBy)?.toString()} <i>(ID, API ne vraća ime)</i></span>
            : formatValue(null)}
        </Row>
        <Row label="Revizija cashback izmena" style={TECHNICAL_ROW_STYLE}>{order.cashbackEditRevision ?? 0}</Row>
      </SectionTable>
    </>
  );
};

OrderCommissionsAndDiscounts.propTypes = {
  order: PropTypes.object,
};

export default OrderCommissionsAndDiscounts;
